import React from "react";
import { useTranslation } from "react-i18next";
import { CheckboxIcon } from "@aureskonnect/react-ui";
import { toast } from "react-toastify";
import { useSnapshot } from "valtio";
import { logger } from "@helpers/logger";

import { store, resetStore, setIsPrinterModified } from "./store";
import {
    setIsLoadingAssociateValidation,
    setIsModalConfirmationOpened,
    store as userStore,
} from "@components/VerticalLayout/store";
import { useFetchGlobal } from "../../../api/hooks";
import { redisPublishPeripheral } from "../../../helpers/peripheralApi";
import {
    checkDisassociatedPeripheral,
    getArrayOfRows,
    buildPrinterObject,
    buildMoneticObject,
    buildCashManagementObject,
} from "./peripheralAssociationUtils";
import {
    accumulateDissociation,
    accumulateAssociation,
} from "./peripheralAssociationBuilder";
import {
    PERIPHERAL_TYPE_CASH_MANAGEMENT,
    PERIPHERAL_TYPE_MONETIC,
    PERIPHERAL_TYPE_PRINTER,
} from "@constants/index";

const PERIPHERAL_API_URL = `${process.env.REACT_APP_API_V2_URL}/settings/application/peripheral`;

type SaleMode = {
    designation: string;
    state: boolean;
};

type SubRow = {
    peripheralType: string;
    peripheralId: string;
    shopId?: string;
    [key: string]: unknown;
};

type RowData = {
    shopId: string;
    subRows?: SubRow[];
    appId?: string;
    customer_ticket?: {
        customerTicketSalesModes?: SaleMode[];
    };
    [key: string]: unknown;
};

type ApplicationRow = {
    shopId: string;
    subRows?: SubRow[];
    appId?: string;
    [key: string]: unknown;
};

type PeripheralItem = {
    Id: string;
    [key: string]: unknown;
};

type ApiResponse = {
    error?: boolean;
    data?: unknown[];
    [key: string]: unknown;
};

type ModeOfSaleApiResponse = {
    data: Array<{
        support_vente: string[];
        "Mode of sale": string;
        [key: string]: unknown;
    }>;
};

type UsePrepheralCardFooterProps = {
    dataIsUpdated: boolean | number;
    setIsEdited: (value: boolean) => void;
    setIsDeviceAssociateModalOpened: (value: boolean) => void;
    isDeviceAssociateModalOpened: boolean;
    rowData: RowData;
    setDataIsUpdated: (value: boolean | number) => void;
    selectedRows?: ApplicationRow[];
    isPrinter?: boolean;
};

export function usePrepheralCardFooter({
    dataIsUpdated,
    setIsEdited,
    setIsDeviceAssociateModalOpened,
    isDeviceAssociateModalOpened,
    rowData,
    setDataIsUpdated,
    selectedRows,
    isPrinter = false,
}: UsePrepheralCardFooterProps) {
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();

    const APPID = "KIOSK";
    const {
        cashManagements,
        selectedRowData,
        monetics,
        printerSelected,
        checkModificationEffect,
    } = useSnapshot(store);

    const {
        userID,
        isLoadingAssociateValidation,
        franchiseID,
        shopID,
        oneShop,
        isModalConfirmationOpened,
        operatorID,
    } = useSnapshot(userStore);
    const uuidUser = localStorage.getItem("uuidUser");

    const [
        isCashManagementModified,
        setIsCashManagementModified,
    ] = React.useState<boolean>(false);
    const [isMoneticModified, setIsMoneticModified] = React.useState<boolean>(
        false
    );
    const [salesModes, setSalesModes] = React.useState<SaleMode[]>([]);
    const shopId = oneShop ? shopID : rowData.shopId;

    const fetchModeOfSale = React.useCallback(async () => {
        const languageHeader = "en-US";
        const headers = new Headers({
            "Accept-Language": languageHeader,
        });
        const requestOptions = {
            method: "GET",
            headers: headers,
        };
        try {
            if (!process.env.REACT_APP_API_V2_URL) {
                throw new Error("REACT_APP_API_V2_URL is not defined");
            }
            const response = await fetchGlobal<ModeOfSaleApiResponse>(
                `${process.env.REACT_APP_API_V2_URL}/settings/general/mode_of_sale?userId=${userID}&shopId=${shopId}&franchiseId=${franchiseID}`,
                requestOptions
            );
            const saleModes = response.data.map((saleMode) => {
                return {
                    // eslint-disable-next-line array-callback-return
                    saleSupports: saleMode.support_vente.map((el: string) => {
                        if (el === "KIOSK")
                            return {
                                designation: saleMode["Mode of sale"],
                                state: false,
                            };
                    }),
                };
            });
            let rowDataSales: SaleMode[] = [];
            let salesSupportMode: string[] = [];
            if (
                rowData["customer_ticket"]?.customerTicketSalesModes !==
                undefined
            ) {
                for (let element of rowData["customer_ticket"]
                    ?.customerTicketSalesModes) {
                    rowDataSales.push(element);
                }
            }
            saleModes.forEach((el) => {
                el.saleSupports.forEach((elt: SaleMode | undefined) => {
                    if (elt !== undefined) {
                        salesSupportMode.push(elt.designation);
                        rowDataSales = rowDataSales.filter(
                            (o) => o.designation !== elt.designation
                        );
                    }
                });
            });

            let sales: SaleMode[] = [];
            let salesModesArr: string[] = [];
            saleModes.forEach((el) => {
                if (
                    rowData["customer_ticket"]?.customerTicketSalesModes ===
                        undefined ||
                    rowData["customer_ticket"]?.customerTicketSalesModes
                        .length === 0
                ) {
                    el.saleSupports.forEach((elt: SaleMode | undefined) => {
                        if (
                            rowData["customer_ticket"]
                                ?.customerTicketSalesModes === undefined ||
                            rowData["customer_ticket"]
                                ?.customerTicketSalesModes.length === 0
                        ) {
                            elt !== undefined && sales.push(elt);
                        }
                    });
                } else {
                    for (let element of rowData["customer_ticket"]
                        ?.customerTicketSalesModes!) {
                        salesModesArr.push(element.designation);
                        sales.push(element);
                    }
                    el.saleSupports.forEach((elt: SaleMode | undefined) => {
                        if (
                            elt !== undefined &&
                            !salesModesArr.includes(elt.designation)
                        ) {
                            sales.push(elt);
                        }
                    });
                }
            });
            sales = [...new Set(sales)];
            for (let sale of rowDataSales) {
                sales = sales.filter((o) => o.designation !== sale.designation);
            }

            setSalesModes(sales);
        } catch (error) {
            logger.error("fetchModeOfSale failed:", error);
        }
        //eslint-disable-next-line
    }, [franchiseID, shopId, userID, rowData]);

    React.useEffect(() => {
        if (isPrinter) {
            fetchModeOfSale();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPrinter]);

    async function RedisPublishPeripheral() {
        try {
            const shopId =
                selectedRows && selectedRows.length !== 0
                    ? selectedRows.filter(
                          (elt: ApplicationRow) =>
                              elt[t("Application")] !== ""
                      )[0]?.shopId
                    : rowData.shopId;
            await redisPublishPeripheral([shopId], fetchGlobal);
        } catch {
            showErrorToast();
        }
    }

    const getAuthHeaders = () => ({
        "Content-Type": "application/json",
        authorization: `Bearer ${localStorage.getItem("jwt")}`,
    });

    const showErrorToast = () => {
        toast.error(`${t("Mistake ! Please try again")}!`, {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 2000,
            theme: "colored",
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
        });
    };

    const buildAssociationObj = (
        items: readonly PeripheralItem[],
        disassociateList: string[],
        subRows: SubRow[],
        buildFn: (el: PeripheralItem) => Record<string, unknown>
    ) => {
        const obj: Record<string, Record<string, unknown>> = {};
        items.forEach((el: PeripheralItem) => {
            if (
                el["Id"] !== undefined &&
                disassociateList.indexOf(el["Id"]) === -1 &&
                subRows.every((elt: SubRow) => elt.peripheralId !== el.Id)
            ) {
                obj[el["Id"]] = buildFn(el);
            }
        });
        return obj;
    };

    async function handleValidateButtonClickEvent() {
        setIsLoadingAssociateValidation(true);

        const dataByType: Record<string, unknown[]> = {
            [PERIPHERAL_TYPE_CASH_MANAGEMENT]: [],
            [PERIPHERAL_TYPE_PRINTER]: [],
            [PERIPHERAL_TYPE_MONETIC]: [],
        };

        const associateDataByType: Record<string, unknown[]> = {
            [PERIPHERAL_TYPE_CASH_MANAGEMENT]: [],
            [PERIPHERAL_TYPE_PRINTER]: [],
            [PERIPHERAL_TYPE_MONETIC]: [],
        };

        let hasError = false;

        const baseParams = {
            userID,
            franchiseId: franchiseID,
            appId: APPID,
            operatorId: operatorID,
            uuidUser,
        };

        const processElement = (element: ApplicationRow, isFromSelectedRows: boolean) => {
            const currentShopId = oneShop ? shopID.toString() : element.shopId;
            const subRows: SubRow[] = (element.subRows as SubRow[]) || [];

            const getDisassociateList = (type: string) =>
                checkDisassociatedPeripheral(
                    subRows
                        .filter((item: SubRow) => item.peripheralType === type)
                        .map((item: SubRow) => item["peripheralId"]),
                    (type === PERIPHERAL_TYPE_PRINTER
                        ? printerSelected
                        : type === PERIPHERAL_TYPE_MONETIC
                        ? monetics
                        : cashManagements
                    ).map((item: PeripheralItem) => item["Id"])
                );

            const peripheralConfig = [
                {
                    key: PERIPHERAL_TYPE_PRINTER,
                    items: printerSelected,
                    shouldAssociate: printerSelected.length > 0,
                    buildFn: (el: PeripheralItem) =>
                        buildPrinterObject(
                            el,
                            isFromSelectedRows,
                            salesModes,
                            t
                        ),
                },
                {
                    key: PERIPHERAL_TYPE_MONETIC,
                    items: monetics,
                    shouldAssociate: isMoneticModified,
                    buildFn: (el: PeripheralItem) => buildMoneticObject(el, t),
                },
                {
                    key: PERIPHERAL_TYPE_CASH_MANAGEMENT,
                    items: cashManagements,
                    shouldAssociate: isCashManagementModified,
                    buildFn: (el: PeripheralItem) => buildCashManagementObject(el, t),
                },
            ];

            const disassociateLists = Object.fromEntries(
                peripheralConfig.map(({ key }) => [
                    key,
                    getDisassociateList(key),
                ])
            );

            const shouldProcessDissociate = isFromSelectedRows
                ? selectedRows!.filter(
                      (elt: ApplicationRow) => elt[t("Application")] !== ""
                  ).length === 1
                : true;

            if (shouldProcessDissociate) {
                const deviceLabel = t("Device");
                for (const { key } of peripheralConfig) {
                    if (key === PERIPHERAL_TYPE_MONETIC && !isMoneticModified)
                        continue;
                    if (
                        key === PERIPHERAL_TYPE_CASH_MANAGEMENT &&
                        !isCashManagementModified
                    )
                        continue;
                    accumulateDissociation(
                        dataByType[key],
                        disassociateLists[key],
                        subRows,
                        element,
                        currentShopId,
                        baseParams,
                        deviceLabel
                    );
                }
            }

            for (const {
                key,
                items,
                shouldAssociate,
                buildFn,
            } of peripheralConfig) {
                if (!shouldAssociate) continue;
                const obj = buildAssociationObj(
                    items,
                    disassociateLists[key],
                    subRows,
                    buildFn
                );
                accumulateAssociation(
                    associateDataByType[key],
                    obj,
                    element,
                    currentShopId,
                    baseParams
                );
            }
        };

        if (selectedRows && selectedRows.length !== 0) {
            const arrayOfRows = getArrayOfRows(selectedRows, t("Application"));
            for (const element of arrayOfRows) {
                processElement(element, true);
            }
        } else {
            processElement(rowData, false);
        }

        try {
            const hasData = (d: Record<string, unknown[]>) =>
                d[PERIPHERAL_TYPE_CASH_MANAGEMENT].length > 0 ||
                d[PERIPHERAL_TYPE_PRINTER].length > 0 ||
                d[PERIPHERAL_TYPE_MONETIC].length > 0;

            if (hasData(dataByType)) {
                const dissociateResult = await fetchGlobal<ApiResponse>(
                    PERIPHERAL_API_URL,
                    {
                        method: "DELETE",
                        headers: getAuthHeaders(),
                        body: JSON.stringify(dataByType),
                    }
                );
                if (dissociateResult.error) hasError = true;
            }

            if (hasData(associateDataByType)) {
                const associateResult = await fetchGlobal<ApiResponse>(
                    PERIPHERAL_API_URL,
                    {
                        method: "POST",
                        headers: getAuthHeaders(),
                        body: JSON.stringify(associateDataByType),
                    }
                );
                if (associateResult.error) hasError = true;
            }

            if (hasError) {
                resetStore();
                showErrorToast();
                setIsLoadingAssociateValidation(false);
                setIsDeviceAssociateModalOpened(isDeviceAssociateModalOpened);
            } else {
                resetStore();
                setIsDeviceAssociateModalOpened(!isDeviceAssociateModalOpened);
                setDataIsUpdated(!dataIsUpdated);
                toast.success(
                    `${t("The association of this device was successful")!}`,
                    {
                        position: toast.POSITION.TOP_CENTER,
                        autoClose: 2000,
                        theme: "colored",
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        icon: (
                            <CheckboxIcon height={25} width={25} fill="white" />
                        ),
                    }
                );
                setIsEdited(false);
                RedisPublishPeripheral();
                setIsLoadingAssociateValidation(false);
            }
        } catch (e) {
            resetStore();
            showErrorToast();
            setIsLoadingAssociateValidation(false);
            setIsDeviceAssociateModalOpened(isDeviceAssociateModalOpened);
        }
    }

    React.useEffect(() => {
        const arrayOfRows = getArrayOfRows(selectedRows ?? [], t("Application"));

        const subRows: SubRow[] | undefined =
            selectedRows && selectedRows.length !== 0 && arrayOfRows.length === 1
                ? arrayOfRows[0].subRows
                : (selectedRowData as RowData).subRows ?? rowData.subRows;

        if (subRows === undefined) {
            setIsCashManagementModified(cashManagements.length > 0);
            setIsMoneticModified(monetics.length > 0);
            return;
        }

        const selectedCashManagementData: string[] = [];
        const selectedMoneticData: string[] = [];

        subRows.forEach((el: SubRow) => {
            if (el.peripheralType === PERIPHERAL_TYPE_CASH_MANAGEMENT) {
                selectedCashManagementData.push(el[t("Peripheral")] as string);
            }
            if (el.peripheralType === PERIPHERAL_TYPE_MONETIC) {
                selectedMoneticData.push(el[t("Peripheral")] as string);
            }
        });

        const isSameCash =
            (cashManagements.length !== 0 &&
                cashManagements[0][t("Designation")] ===
                    selectedCashManagementData[0]) ||
            (cashManagements.length === 0 &&
                selectedCashManagementData.length === 0);

        const isSameMonetic =
            (monetics.length !== 0 &&
                monetics[0][t("Designation")] === selectedMoneticData[0]) ||
            (monetics.length === 0 && selectedMoneticData.length === 0);

        setIsCashManagementModified(!isSameCash);
        setIsMoneticModified(!isSameMonetic);
        // eslint-disable-next-line
    }, [selectedRowData, cashManagements, monetics]);

    return {
        t,
        isLoadingAssociateValidation,
        isModalConfirmationOpened,
        checkModificationEffect,
        handleValidateButtonClickEvent,
        setIsModalConfirmationOpened,
        setIsPrinterModified,
        resetStore,
    };
}
